import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Journey } from 'Global/Journey/Types/Journey';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import useQueryState from 'Hooks/useQueryState';
import { appConfigStore } from 'Models/AppConfigStore';
import AdditionalBookingSectionEntity from 'Models/Entities/AdditionalBookingSectionEntity';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { FreightTabDto, FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { UnitConverter } from 'Util/UnitOfMeasurementUtils';
import { ErrorCard } from 'Views/Components/_HumanWritten/ErrorCard/ErrorCard';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import InputCounter from 'Views/Components/_HumanWritten/PassengerAmount/InputCounter';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Flex } from 'Views/Components/Flex/Flex';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { TextInput } from 'Views/Components/New/TextInput';
import Spinner from 'Views/Components/Spinner/Spinner';
import { FreightBookingDetailState } from '../../BookingWizardData/FreightBookingWizardData';
import {
	BulkFreightDetailsFormActionCreators as Abracadabra,
	BulkFreightDetailsFormAction,
} from './BulkFreightDetailsFormState';
import { roundCubic } from './CalculationHelpers';
import MeasurementInput from './MeasurementInput';
import { confirmDeleteFreightItem } from './Utils/confirmDeleteFreightItem';
import { handleFreightCalculationFetch } from './Utils/handleFreightCalculationFetch';

interface Props {
	freightElements: FreightTabDto;
	addOnSections: AdditionalBookingSectionEntity[];
	journey: Journey;
	userId: string;
	state: FreightBookingDetailState;
	dispatch: (action: BulkFreightDetailsFormAction) => void;
	hasMultipleItems: boolean;
}

export default function ShipmentCardExpanded({
	freightElements,
	addOnSections,
	journey,
	userId,
	state,
	dispatch,
	hasMultipleItems,
}: Props) {
	const intl = useIntl();
	const { isIpad } = useDeviceDetect();
	const {
		freightTypes: freightTypeOptions,
		freightCategoryTypes: freightCategoryOptions,
		freightHandlingTypes: freightHandlingOptions,
	} = freightElements;
	const { errors } = state;

	const cubicVolume = useMemo(
		() => calculateCubicVolume(state),
		[state.length, state.width, state.height, state.quantity],
	);

	const showDimensions = useMemo(() => {
		if (appConfigStore.freightDimensionsHiddenOnStaticPriceEnabled) {
			const freightTypes = Object.values(freightTypeOptions ?? {}).flat();
			const match = freightTypes.find(ft => ft.id === state.freightTypeId);
			if (match) {
				return !match.hasStaticPricing; // show dimensions when not using static price
			}
			return false;
		}
		return true;
	}, [appConfigStore.freightDimensionsHiddenOnStaticPriceEnabled, state.freightTypeId]);

	const allTheSame = (innerWizardData: FreightBookingDetailState, oldFreightType?: FreightTypeDto) => {
		const oldLength = oldFreightType?.standardLength;
		const oldWidth = oldFreightType?.standardWidth;
		const oldHeight = oldFreightType?.standardHeight;
		const oldWeight = oldFreightType?.minimumWeight;
		const oldDescription = stringNotEmpty(oldFreightType?.standardDescription)
			? oldFreightType?.standardDescription
			: oldFreightType?.label;

		return (
			(innerWizardData.length === oldLength || !innerWizardData.length) &&
			(innerWizardData.width === oldWidth || !innerWizardData.width) &&
			(innerWizardData.height === oldHeight || !innerWizardData.height) &&
			(innerWizardData.weight === oldWeight || !innerWizardData.weight) &&
			(innerWizardData.description === oldDescription || !innerWizardData.description)
		);
	};

	const onChangeFreightType = (freightTypeId: string, oldValue: string) => {
		const freightType = Object.values(freightTypeOptions ?? {})
			.flat()
			.find(x => x.id === freightTypeId);
		if (!freightType) {
			return;
		}

		dispatch(Abracadabra.updateDetailFreightType(state.id, freightTypeId));

		const oldFreightType = Object.values(freightTypeOptions ?? {})
			.flat()
			.find(x => x.id === oldValue);

		if (allTheSame(state, oldFreightType)) {
			dispatch(Abracadabra.updateDetailLength(state.id, freightType.standardLength ?? null));
			dispatch(Abracadabra.updateDetailWidth(state.id, freightType.standardWidth ?? null));
			dispatch(Abracadabra.updateDetailHeight(state.id, freightType.standardHeight ?? null));
			dispatch(Abracadabra.updateDetailWeight(state.id, freightType.minimumWeight ?? null));
			dispatch(
				Abracadabra.updateDetailDescription(
					state.id,
					stringNotEmpty(freightType.standardDescription)
						? freightType.standardDescription
						: freightType.label,
				),
			);
		}
	};

	const onChangeFreightCategory = (oldValue: string) => {
		const oldFreightType = Object.values(freightTypeOptions ?? {})
			.flat()
			.find(x => x.id === oldValue);

		if (allTheSame(state, oldFreightType)) {
			dispatch(Abracadabra.updateDetailLength(state.id, null));
			dispatch(Abracadabra.updateDetailWidth(state.id, null));
			dispatch(Abracadabra.updateDetailHeight(state.id, null));
			dispatch(Abracadabra.updateDetailWeight(state.id, null));
			dispatch(Abracadabra.updateDetailDescription(state.id, ''));
		}
	};

	const onChange = (dimension: 'length' | 'width' | 'height' | 'weight', value: number) => {
		state[dimension] = value;
	};

	const handleDelete = useCallback(async () => {
		if (await confirmDeleteFreightItem(state)) {
			dispatch(Abracadabra.deleteDetail(state.id));
		}
	}, [state]);

	const handleSave = useCallback(() => {
		dispatch(Abracadabra.validateAndTryCollapse(state.id));
	}, [state.id]);

	const displayedAddOnSections = useMemo(() => {
		return addOnSections
			.filter(
				addOnSection => addOnSection.additionalBookingOptions.filter(addOn => addOn.freightAddOn).length > 0,
			)
			.map(addOnSection => ({
				id: addOnSection.id,
				additionalBookingOptions: addOnSection.additionalBookingOptions.filter(addOn => addOn.freightAddOn),
			}));
	}, [addOnSections]);

	const freightCategorySelectOptions = freightCategoryOptions
		.filter(x => Object.keys(freightTypeOptions).includes(x.id) && freightTypeOptions[x.id].length > 0)
		.map(cat => ({ value: cat.id, label: cat.label }));

	const freightTypeSelectOptions =
		freightTypeOptions[state.freightCategoryId]?.map(type => ({ value: type.id, label: type.label })) ?? [];

	const handlingSelectOptions = freightHandlingOptions?.map(type => ({ value: type.id, label: type.label })) ?? [];

	return (
		<div className="shipment-details__container flex flex-col gap-3">
			<section className="shipment-section booking-section-card">
				<header className="booking-section-header">
					<span className="booking-section-heading__icon">
						<Icon name="description" useMaterialIcons />
					</span>
					<div className="booking-section-header__text">
						<h4 className="booking-section-title">
							<FormattedMessage
								id="freight-booking-wizard__shipment-tab__freight-description-section__label"
								defaultMessage="Shipment description"
							/>
						</h4>
					</div>
				</header>
				<div className="shipment-section-body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
					<div data-test-id="freight-category-field">
						<SelectInput
							label={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-category__label',
								defaultMessage: 'Freight category',
							})}
							placeholder={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-category__placeholder',
								defaultMessage: 'Select freight category',
							})}
							isSearchable
							required
							options={freightCategorySelectOptions}
							value={freightCategorySelectOptions.find(o => o.value === state.freightCategoryId) ?? null}
							onChange={(option: any) => {
								const oldFreightType = state.freightTypeId;
								onChangeFreightCategory(oldFreightType);
								if (typeof option?.value === 'string') {
									dispatch(Abracadabra.updateDetailFreightCategory(state.id, option.value));
								}
							}}
							error={errors?.freightCategoryId}
						/>
					</div>
					<div data-test-id="freight-type-field">
						<SelectInput
							label={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-type__label',
								defaultMessage: 'Freight type',
							})}
							placeholder={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-type__placeholder',
								defaultMessage: 'Select freight type',
							})}
							isSearchable
							required
							isDisabled={!state.freightCategoryId}
							options={freightTypeSelectOptions}
							value={freightTypeSelectOptions.find(o => o.value === state.freightTypeId) ?? null}
							onChange={(option: any) => {
								const oldValue = state.freightTypeId;
								if (typeof option?.value === 'string') {
									onChangeFreightType(option.value, oldValue ?? '');
								}
							}}
							error={errors?.freightTypeId}
						/>
					</div>
					<div data-test-id="freight-quantity-field">
						<NumberInput
							label={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-quantity__label',
								defaultMessage: 'Quantity',
							})}
							placeholder={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-quantity__placeholder',
								defaultMessage: 'Enter quantity',
							})}
							required
							allowDecimals={false}
							allowNegative={false}
							value={state.quantity ?? undefined}
							onChange={value => {
								if (isNotNullOrUndefined(value) && !Number.isNaN(value)) {
									dispatch(Abracadabra.updateDetailQuantity(state.id, value));
								}
							}}
							error={errors?.quantity}
						/>
					</div>
					{isNotNullOrUndefined(freightHandlingOptions) && freightHandlingOptions.length > 0 && (
						<SelectInput
							label={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-handling__label',
								defaultMessage: 'Handling',
							})}
							placeholder={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-handling__placeholder',
								defaultMessage: 'Select handling',
							})}
							isMulti
							isSearchable={false}
							options={handlingSelectOptions}
							value={handlingSelectOptions.filter(o => (state.handlingIds ?? []).includes(o.value))}
							onChange={(selected: any) => {
								dispatch(
									Abracadabra.updateDetailFreightHandlings(
										state.id,
										(selected ?? []).map((o: any) => o.value) as [],
									),
								);
							}}
							error={errors?.handlingIds}
						/>
					)}
					<div data-test-id="freight-description-field">
						<TextInput
							label={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-description__label',
								defaultMessage: 'Description',
							})}
							placeholder={intl.formatMessage({
								id: 'freight-booking-wizard__shipment-tab__freight-description__placeholder',
								defaultMessage: 'Enter description',
							})}
							required
							value={state.description ?? ''}
							onChange={e => dispatch(Abracadabra.updateDetailDescription(state.id, e.target.value))}
							error={errors?.description}
						/>
					</div>
				</div>
				{showDimensions && (
					<div className="freight-dimensions" data-test-id="freight-dimensions-section">
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								<Icon name="straighten" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">
									<FormattedMessage
										id="freight-booking-wizard__shipment-tab__freight-dimensions-section__label"
										defaultMessage="Shipment dimensions"
									/>
								</h4>
							</div>
						</header>
						<div className="freight-dimensions__grid grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
							<NumberInput
								label={intl.formatMessage({
									id: 'freight-booking-wizard__shipment-tab__freight-weight__label',
									defaultMessage: 'Weight (per unit)',
								})}
								placeholder="0"
								required
								allowNegative={false}
								endAdornment={whiteLabelStore.massUnitLabel}
								value={state.weight ?? undefined}
								onChange={value => dispatch(Abracadabra.updateDetailWeight(state.id, value ?? null))}
								error={errors?.weight}
							/>
							<MeasurementInput
								unitType={whiteLabelStore.systemMeasurementUnit}
								value={state.length ?? 0}
								onChange={input => {
									dispatch(Abracadabra.updateDetailLength(state.id, input));
									onChange('length', input);
								}}
								preLabel="Length (per unit)"
								required
								error={errors?.length}
							/>
							<MeasurementInput
								unitType={whiteLabelStore.systemMeasurementUnit}
								value={state.width ?? 0}
								onChange={input => {
									dispatch(Abracadabra.updateDetailWidth(state.id, input));
									onChange('width', input);
								}}
								preLabel="Width (per unit)"
								required
								error={errors?.width}
							/>
							<MeasurementInput
								unitType={whiteLabelStore.systemMeasurementUnit}
								value={state.height ?? 0}
								onChange={input => {
									dispatch(Abracadabra.updateDetailHeight(state.id, input));
									onChange('height', input);
								}}
								preLabel="Height (per unit)"
								required
								error={errors?.height}
							/>
							<TextInput
								label={intl.formatMessage({
									id: 'freight-booking-wizard__shipment-tab__freight-total-weight__label',
									defaultMessage: 'Total weight',
								})}
								placeholder="0"
								disabled
								value={`${((state.weight ?? 0) * (state.quantity ?? 0)).toFixed(0)} ${whiteLabelStore.massUnitLabel}`}
							/>
							<TextInput
								label={intl.formatMessage({
									id: 'freight-booking-wizard__shipment-tab__freight-total-volume__label',
									defaultMessage: 'Total cubic',
								})}
								placeholder="0"
								disabled
								value={cubicVolume}
							/>
						</div>
					</div>
				)}
				{displayedAddOnSections.length > 0 && (
					<div className="freight-add-ons" data-test-id="freight-add-ons-section">
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								<Icon name="add_shopping_cart" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">
									<FormattedMessage
										id="freight-booking-wizard__shipment-tab__freight-add-ons-section__label"
										defaultMessage="Add ons"
									/>
								</h4>
							</div>
						</header>
						<div className="freight-add-ons__list">
							{displayedAddOnSections.map(addOnSection => (
								<React.Fragment key={addOnSection.id}>
									{addOnSection.additionalBookingOptions.map(addOn => {
										const amount = state.addOns.find(x => x.optionId === addOn.id)?.amount ?? 0;
										return (
											<div className="input-counter--box bg-white">
												<InputCounter
													title={addOn.name}
													subtitle={
														!addOn.displayInfoInTooltip ? addOn.information : undefined
													}
													tooltip={addOn.displayInfoInTooltip ? addOn.information : undefined}
													amount={amount}
													subtractDisabled={amount === 0}
													addDisabled={addOn.isSingular ? amount > 0 : false}
													onAdd={() =>
														dispatch(
															Abracadabra.updateDetailIncrementAddOn(state.id, addOn.id),
														)
													}
													onSubtract={() =>
														dispatch(
															Abracadabra.updateDetailDecrementAddOn(state.id, addOn.id),
														)
													}
												/>
											</div>
										);
									})}
								</React.Fragment>
							))}
						</div>
					</div>
				)}
				<Flex justify="flex-end" className="mt-3">
					{hasMultipleItems && (
						<Flex justify="space-between" gap="xs">
							<Button
								className="edit-passenger__button"
								colors={Colors.Red}
								display={Display.Text}
								onClick={handleDelete}
							>
								Delete
							</Button>
							<Button
								className="edit-passenger__button"
								colors={Colors.Primary}
								display={Display.Solid}
								newStyles
								onClick={handleSave}
								buttonProps={{ 'data-test-id': 'freight-shipment-save-button' } as any}
							>
								Calculate cost
							</Button>
						</Flex>
					)}
					{!hasMultipleItems && (
						<Button
							className="edit-passenger__button"
							colors={Colors.Primary}
							display={Display.Solid}
							newStyles
							onClick={handleSave}
							buttonProps={{ 'data-test-id': 'freight-shipment-save-button' } as any}
						>
							Calculate cost
						</Button>
					)}
				</Flex>
			</section>
			{isIpad && (
				<ShipmentCalculation
					journey={journey}
					userId={userId}
					state={state}
					onBeforeClick={() => dispatch(Abracadabra.validateFreightDetail(state.id))}
				/>
			)}
		</div>
	);
}

const calculateCubicVolume = (state: FreightBookingDetailState) => {
	const { length, width, height, quantity } = state;

	const l = length ?? 0;
	const w = width ?? 0;
	const h = height ?? 0;

	const dimensions = {
		length: l,
		width: w,
		height: h,
		unitOfMeasurement: whiteLabelStore.systemMeasurementUnit,
	};

	const value = roundCubic(UnitConverter.getCubic(dimensions));

	return `${(quantity ?? 0) * value} ${whiteLabelStore.volumeUnitLabel}`;
};

interface ShipmentCalculationProps {
	journey: Journey;
	userId: string;
	state: FreightBookingDetailState;
	onBeforeClick?: () => void;
}

function ShipmentCalculation({ journey, userId, state, onBeforeClick }: ShipmentCalculationProps) {
	const intl = useIntl();
	// Calculation will only show when there are no errors in state, and has been triggered
	const [triggerCalculation, setTriggerCalculation] = useState(false);

	const { state: freightCalculationRequest, query } = useQueryState(() =>
		handleFreightCalculationFetch({
			userId,
			journey,
			freightTypeId: state.freightTypeId,
			freightCategoryId: state.freightCategoryId,
			quantity: state.quantity,
			weight: state.weight,
			width: state.width,
			height: state.height,
			length: state.length,
		}),
	);

	const hasErrors = Object.entries(state.errors).length > 0;

	useEffect(() => {
		if (!hasErrors && triggerCalculation) {
			query();
		}
		setTriggerCalculation(false);
	}, [triggerCalculation]);

	const onClick = () => {
		onBeforeClick?.();
		setTriggerCalculation(true);
	};

	const buttonText = useMemo(() => {
		if (freightCalculationRequest.status === 'idle') {
			return 'Calculate shipment';
		}
		return 'Re-calculate shipment';
	}, [freightCalculationRequest.status]);

	return (
		<div className="shipment-details-calculation__container">
			<Button display={Display.Outline} colors={Colors.Black} onClick={onClick}>
				{buttonText}
			</Button>
			{hasErrors && (
				<ErrorCard
					className=""
					title={intl.formatMessage({
						id: 'freight-booking-wizard__shipment-tab__incomplete-error__title',
						defaultMessage: 'Incomplete shipment details',
					})}
					subtitle={intl.formatMessage({
						id: 'freight-booking-wizard__shipment-tab__incomplete-error__subtitle',
						defaultMessage: 'Please complete the shipment details before calculating the shipment cost.',
					})}
				/>
			)}
			{freightCalculationRequest.status === 'loading' && (
				<div className="mt-xs">
					<Spinner />
				</div>
			)}
			{freightCalculationRequest.status === 'success' && !hasErrors && (
				<div className="shipment-details__calculation">
					<h4>
						<FormattedMessage
							id="freight-booking-wizard__shipment-tab__shipment-cost__heading"
							defaultMessage="Shipment cost"
						/>
					</h4>
					<h2>{formatPriceDisplay(freightCalculationRequest.data.totalPrice, false, true, false)}</h2>
					<p>{freightCalculationRequest.data.calculation}</p>
				</div>
			)}
		</div>
	);
}
